import React from 'react';
import PropTypes from 'prop-types';

/**
 * DataTile - Reusable metric card with trend indicator
 *
 * Used on the dashboard to display key metrics with optional
 * trend information and click-to-navigate functionality.
 *
 * @param {Object} props
 * @param {string} props.icon - Emoji or icon to display
 * @param {number|string} props.value - Main metric value
 * @param {string} props.label - Description label
 * @param {string} props.trend - Trend text (e.g., "5 in last 24h")
 * @param {string} props.trendDirection - 'up', 'down', or 'neutral'
 * @param {string} props.variant - Color variant: 'primary', 'success', 'info', 'warning'
 * @param {Function} props.onClick - Click handler for navigation
 * @param {string} props.subtitle - Secondary text (shown when no trend)
 */
const DataTile = ({
  icon,
  value,
  label,
  trend,
  trendDirection = 'neutral',
  variant = 'primary',
  onClick,
  subtitle,
}) => {
  const getTrendIcon = () => {
    if (trendDirection === 'up') return '↑';
    if (trendDirection === 'down') return '↓';
    return '→';
  };

  const getTrendClass = () => {
    if (trendDirection === 'up') return 'trend-positive';
    if (trendDirection === 'down') return 'trend-negative';
    return 'trend-neutral';
  };

  const handleKeyDown = (e) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`metric-card ${variant} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="metric-icon">{icon}</div>
      <div className="metric-content">
        <div className="metric-value">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div className="metric-label">{label}</div>
        {trend && (
          <div className={`metric-trend ${getTrendClass()}`}>
            {getTrendIcon()} {trend}
          </div>
        )}
        {subtitle && !trend && (
          <div className="metric-subtitle">{subtitle}</div>
        )}
      </div>
    </div>
  );
};

DataTile.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  trend: PropTypes.string,
  trendDirection: PropTypes.oneOf(['up', 'down', 'neutral']),
  variant: PropTypes.oneOf(['primary', 'success', 'info', 'warning']),
  onClick: PropTypes.func,
  subtitle: PropTypes.string,
};

DataTile.defaultProps = {
  trend: null,
  trendDirection: 'neutral',
  variant: 'primary',
  onClick: null,
  subtitle: null,
};

export default DataTile;
